import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { receivingApi } from "../../shared/receivingApi";
import InputTextOnly from "../../components/controls/InputTextOnly";
import SelectMenu from "../../components/controls/SelectMenu";
import TextArea from "../../components/controls/TextArea";
import DatePickerInput from "../../components/controls/DatePickerInput";
import type { SupplierOption, ReceivingInvoice, ReceivingStatus } from "../../types/receiving";
import type { ApiErrorPayload, SelectOption } from "../../types/api";

interface ReceivingForm {
  supplier_id: number | "";
  invoice_number: string;
  invoice_date: string;
  notes: string;
}

type ReceivingFilterStatus = ReceivingStatus | "";

const formatDate = (date: Date | null) =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : "";

const emptyForm: ReceivingForm = {
  supplier_id: "",
  invoice_number: "",
  invoice_date: formatDate(new Date()),
  notes: "",
};
const statuses: Array<SelectOption<ReceivingFilterStatus>> = [
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  { value: "void", label: "Void" },
];
const statusStyles: Record<ReceivingStatus, string> = {
  open: "bg-amber-50 text-amber-700",
  closed: "bg-emerald-50 text-emerald-700",
  void: "bg-gray-100 text-gray-600",
};
const statusLabel = (value: ReceivingFilterStatus) =>
  statuses.find((option) => option.value === value)?.label ?? value;

const apiErrorMessage = (error: ApiErrorPayload, fallback: string) =>
  error.message ?? Object.values(error.errors ?? {}).flat()[0] ?? fallback;

export default function ReceivingInvoices() {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [invoices, setInvoices] = useState<ReceivingInvoice[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState<ReceivingFilterStatus>("");
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (searchTerm = searchRef.current?.value ?? "") => {
    setLoading(true);
    const result = await receivingApi.invoices(searchTerm, status);
    if (result.success) setInvoices(result.data.data || []);
    else setError(result.data?.message || "Could not load receiving invoices.");
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    receivingApi.suppliers().then((r) => r.success && setSuppliers(r.data));
  }, []);

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!form.supplier_id || !form.invoice_number || !form.invoice_date) {
      setError("Supplier, invoice number, and invoice date are required.");
      return;
    }
    const result = await receivingApi.createInvoice({
      ...form,
      supplier_id: Number(form.supplier_id),
    });
    if (result.success) navigate(`/receiving/${result.data.id}`);
    else
      setError(
        apiErrorMessage(result.data, "Could not create invoice.")
      );
  };

  return (
    <div className="p-6 space-y-5">
      <section className="apollo-card content-card rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              Inbound inventory
            </p>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">Receiving invoices</h1>
            <p className="mt-1 text-sm text-gray-500">
              Create a supplier receipt or resume work already on the dock.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white hover:bg-primary-700"
          >
            {showCreate ? "Close" : "New receiving invoice"}
          </button>
        </div>
      </section>

      {showCreate && (
        <form onSubmit={create} className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="text-lg font-semibold text-gray-900">Supplier invoice</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <SelectMenu
              title="Supplier"
              name="supplier_id"
              options={suppliers}
              value={
                suppliers.find((supplier) => Number(supplier.value) === Number(form.supplier_id)) ||
                null
              }
              handleInputChange={(event) =>
                setForm({ ...form, supplier_id: event.target.value?.value ?? "" })
              }
              placeholder="Select supplier"
            />
            <InputTextOnly
              label="Supplier invoice number"
              name="invoice_number"
              labelOn={true}
              value={form.invoice_number}
              onChange={(event) => setForm({ ...form, invoice_number: event.target.value })}
              className="w-full"
              required
            />
            <DatePickerInput
              label="Invoice date"
              name="invoice_date"
              labelOn={true}
              value={form.invoice_date}
              onChange={(event) =>
                setForm({ ...form, invoice_date: formatDate(event.target.value) })
              }
            />
            <TextArea
              label="Notes"
              name="notes"
              labelOn={true}
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
              rows={3}
            />
          </div>
          <button className="mt-5 rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white">
            Create and receive
          </button>
        </form>
      )}

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>
      )}

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as ReceivingFilterStatus)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
          >
            {statuses.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="flex flex-1 rounded-md">
            <div className="relative flex flex-grow items-stretch focus-within:z-10">
              <input
                type="search"
                name="searchReceiving"
                id="searchReceiving"
                className="block w-full rounded-lg rounded-l-md border border-gray-300 pl-2 sm:text-sm rounded-tr-none rounded-br-none"
                placeholder="Supplier or invoice number"
                ref={searchRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void load();
                }}
              />
            </div>
            <button
              type="button"
              className="-ml-px relative inline-flex items-center space-x-2 border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 rounded-r-md hover:bg-gray-100 focus:outline-none focus:ring-1"
              onClick={() => load()}
            >
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="mt-5 divide-y divide-gray-100">
          {loading ? (
            <p className="py-8 text-center text-gray-500">Loading receivings…</p>
          ) : invoices.length === 0 ? (
            <p className="py-8 text-center text-gray-500">
              No {status ? statusLabel(status).toLowerCase() : "receiving"} invoices.
            </p>
          ) : (
            invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="grid w-full gap-2 py-4 sm:grid-cols-[1.3fr_1fr_auto] sm:items-center sm:px-2"
              >
                <div>
                  <p className="font-semibold text-gray-900">{invoice.supplier?.name}</p>
                  <p className="text-sm text-gray-500">Invoice {invoice.invoice_number}</p>
                </div>
                <div>
                  <p className="font-mono text-sm tabular-nums text-gray-900">
                    ${invoice.total_cost}
                  </p>
                  <p className="text-xs text-gray-500">{invoice.items?.length || 0} lines</p>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[invoice.status]}`}
                  >
                    {statusLabel(invoice.status)}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate(`/receiving/${invoice.id}`)}
                    className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-600"
                  >
                    {invoice.status === "open" ? "Continue" : "View"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
